import Phaser from 'phaser';

export interface ParallaxLayerConfig {
  /** 0 = static backdrop, 1 = moves with the world. Smaller = further away. */
  factor: number;
  /** Placeholder silhouette colour until art lands. */
  color: number;
  /** Building tops start this fraction down the screen (0 = top, 1 = bottom). */
  topFrac: number;
  /**
   * Optional texture/asset key for painted background art.
   * When set and the texture exists in the scene, this layer renders
   * the image as a tiling sprite instead of the baked colour skyline.
   * Convention: keys match public/assets/backgrounds/ filenames
   * (e.g. 'bg_s1_far', 'bg_s1_mid', 'bg_s1_near').
   */
  image?: string;
}

export interface ParallaxConfig {
  levelWidth: number;
  sky: number;
  layers: ParallaxLayerConfig[];
  /**
   * Optional texture key for a full-sky painted image.
   * TODO(MY-2075): render skyImage when present (art drops in follow-up).
   */
  skyImage?: string;
}

/**
 * Pure: horizontal tile offset for a layer given the camera scroll + factor.
 * Negative so the layer appears to move opposite the camera (depth illusion).
 */
export function parallaxTilePosition(cameraScrollX: number, factor: number): number {
  return cameraScrollX * factor;
}

/**
 * Pure: resolve which texture key to use for a parallax layer.
 *
 * Returns the layer's `image` key when it is non-empty AND passes the
 * `textureExists` check; otherwise returns the baked-skyline fallback key.
 * Accepting `textureExists` as a parameter keeps this testable in Node
 * without importing Phaser.
 */
export function resolveLayerTextureKey(
  layer: ParallaxLayerConfig,
  sceneKey: string,
  layerIndex: number,
  textureExists: (key: string) => boolean,
): { key: string; useImage: boolean } {
  if (layer.image && textureExists(layer.image)) {
    return { key: layer.image, useImage: true };
  }
  return { key: `px-${sceneKey}-${layerIndex}`, useImage: false };
}

/**
 * ParallaxBackground — N depth layers of repeating skyline silhouettes or
 * painted art.
 *
 * Each layer is a screen-pinned TileSprite (scrollFactor 0); we drive its
 * `tilePositionX` from the camera's scroll × the layer factor. When a layer's
 * `image` key resolves to a loaded texture the painted art tiles horizontally;
 * otherwise a colour skyline is baked deterministically from Graphics (no art
 * needed, stable across runs).
 */
export class ParallaxBackground {
  private readonly layers: { sprite: Phaser.GameObjects.TileSprite; factor: number }[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    cfg: ParallaxConfig,
  ) {
    const { width, height } = scene.scale;
    scene.cameras.main.setBackgroundColor(cfg.sky);

    cfg.layers.forEach((layer, i) => {
      const { key, useImage } = resolveLayerTextureKey(layer, scene.scene.key, i, (k) =>
        scene.textures.exists(k),
      );

      if (!useImage && !scene.textures.exists(key)) {
        this.bakeSkyline(key, 512, height, layer);
      }

      const ts = scene.add
        .tileSprite(0, 0, width, height, key)
        .setOrigin(0, 0)
        .setScrollFactor(0)
        .setDepth(-100 + i);
      if (useImage) {
        // Painted backdrops are complete scenes — scale the tile to fill the
        // screen height (preserving aspect) so they don't crop to native size
        // or tile vertically. They still tile horizontally as the camera pans.
        const src = scene.textures.get(key).getSourceImage() as { height?: number };
        if (src && src.height) {
          const s = height / src.height;
          ts.tileScaleX = s;
          ts.tileScaleY = s;
        }
      }
      this.layers.push({ sprite: ts, factor: layer.factor });
    });
  }

  private bakeSkyline(key: string, w: number, h: number, layer: ParallaxLayerConfig): void {
    const g = this.scene.add.graphics();
    g.fillStyle(layer.color, 1);
    let x = 0;
    while (x < w) {
      const bw = 24 + ((x * 37) % 48);
      const bh = h * (layer.topFrac + ((x * 53) % 30) / 100);
      g.fillRect(x, h - bh, bw - 4, bh);
      x += bw;
    }
    g.generateTexture(key, w, h);
    g.destroy();
  }

  update(): void {
    const sx = this.scene.cameras.main.scrollX;
    this.layers.forEach((l) => {
      l.sprite.tilePositionX = parallaxTilePosition(sx, l.factor);
    });
  }
}
